/** Copyright 2025 SAP SE or an SAP affiliate company. All rights reserved. */
import { ConfigIdentifier, Environment, PxApi, ThemeId } from '@sap-px/pxapi';
import * as PxApiTypes from '@sap-px/pxapi/dist/api/common/Types';
import { getStoredOrBrowserLanguage } from '../utils/langUtils';
import * as storageUtils from '@/utils/storageUtils';
import { Dispatch } from '@reduxjs/toolkit';
import { actions } from '@/store/feedbackSlice';

const createFeedbackService = () => {
    let pxApi: PxApi | null = null;
    let resolveSurveyInvitation: PxApiTypes.SurveyInvitationDialogDecision | null = null;
    const currentLanguage = getStoredOrBrowserLanguage();
    let dispatch: Dispatch | null = null;

    const setDispatch = (dispatchFn: Dispatch) => {
        dispatch = dispatchFn;
    };

    let appContextData: PxApiTypes.AppContextData = {
        appId: 'deployment hub',
        appTitle: 'deployment hub',
        appVersion: '1.0',
        appSupportInfo: '',
        technicalAppComponentId: '',
        appFrameworkId: 'React',
        appFrameworkVersion: '1.0',
        theme: ThemeId.sap_fiori_3,
        languageTag: currentLanguage === 'fallback' ? 'English' : currentLanguage,
    };

    const initializePxApi = async (configData: { configUrl: string; unitId: string; environment: string }) => {
        const userInfo = storageUtils.getItem('user');

        const configIdentifier: ConfigIdentifier = {
            unitId: configData.unitId,
            environment: configData.environment as Environment,
            configUrl: configData.configUrl,
        };
        const tenantInfo: PxApiTypes.TenantInfo = {
            tenantId: userInfo?.crm_account_id,
            tenantRole: configData.environment as Environment,
        };

        try {
            const pxApiInstance = new PxApi();
            const isInitialized = await pxApiInstance.initialize(tenantInfo, configIdentifier, undefined, surveyInvitationDialogCallback);
            if (!isInitialized) {
                return false;
            }
            pxApiInstance.currentThemeId = ThemeId.sap_fiori_3;
            pxApi = pxApiInstance;
            return isInitialized;
        } catch {
            return false;
        }
    };

    const declineFeedback = () => {
        const resultData = {
            appContextData: appContextData,
            surveyUser: false,
        };
        resolveSurveyInvitation?.(resultData);
    };

    const provideFeedback = () => {
        const resultData = {
            appContextData: appContextData,
            surveyUser: true,
        };
        resolveSurveyInvitation?.(resultData);
    };

    const surveyInvitationDialogCallback: PxApiTypes.SurveyInvitationDialogCallback = async eventData => {
        return new Promise(resolve => {
            resolveSurveyInvitation = resolve;
            if (eventData.showInvitation) {
                if (dispatch) {
                    dispatch(actions.setPushType(eventData.pushType));
                    dispatch(actions.setInvitationDialogStatus(true));
                }
            } else {
                provideFeedback();
            }
        });
    };

    const triggerInstantFeedback = async (configData: { areaId: string; triggerName: string }) => {
        try {
            await pxApi?.requestPush({
                areaId: configData.areaId ?? '',
                triggerName: configData.triggerName ?? '',
            });
        } catch (e) {
            console.log(e);
        }
    };

    const triggerUserInitiatedFeedback = async () => {
        try {
            await pxApi?.openSurvey(appContextData);
        } catch (e) {
            console.log(e);
        }
    };

    const isUserInitiatedFeedback = () => pxApi?.isUserInitiatedFeedbackEnabled ?? false;

    const modifyAppContextData = (param: Partial<PxApiTypes.AppContextData>) => {
        appContextData = {
            ...appContextData,
            ...param,
        };
    };

    const resetAppContextData = (originalAppContextData: PxApiTypes.AppContextData) => {
        appContextData = originalAppContextData;
    };

    const destroy = () => {
        pxApi?.destroy();
        pxApi = null;
    };

    return {
        initializePxApi,
        declineFeedback,
        provideFeedback,
        triggerUserInitiatedFeedback,
        triggerInstantFeedback,
        isUserInitiatedFeedback,
        modifyAppContextData,
        resetAppContextData,
        destroy,
        setDispatch,
    };
};

export const feedbackService = createFeedbackService();

export const setFeedbackServiceDispatch = (dispatch: Dispatch) => {
    feedbackService.setDispatch(dispatch);
};
